import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CalendarCheck, Repeat, ArrowRight, CheckCircle, Gift, TrendingDown, Sparkles, Lock, FileText, Download, BadgePercent, Banknote } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

// Import the actual pages
import { EspaceParticulierFlow } from "./EspaceParticulierFlow";
import { AbonnementFlow } from "./AbonnementFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple Factures Component for the choice page
const MesFacturesSimple = ({ token }) => {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFactures = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/mes-factures`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFactures(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFactures();
  }, [token]);

  if (loading || factures.length === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  };

  const getPrestationLabel = (type) => {
    const labels = {
      menage: "Ménage",
      vitres: "Nettoyage vitres",
      remise_etat: "Remise en état",
      "Abonnement menage": "Abonnement ménage",
      "Abonnement vitres": "Abonnement vitres"
    };
    return labels[type] || type || "Prestation";
  };

  return (
    <div className="mt-12 max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl border-2 border-blue-100 p-6 shadow-sm">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-[#003264] rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#003264]">Mes factures</h3>
            <p className="text-sm text-gray-500">{factures.length} document{factures.length > 1 ? "s" : ""} disponible{factures.length > 1 ? "s" : ""}</p>
          </div>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {factures.slice(0, 6).map(facture => (
            <div key={facture.id} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FileText className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#003264] text-sm">
                    {getPrestationLabel(facture.prestation_type)}
                  </p>
                  {facture.prestation_date && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(facture.prestation_date)}
                    </p>
                  )}
                  {facture.description && (
                    <p className="text-xs text-gray-400 mt-1 truncate">{facture.description}</p>
                  )}
                </div>
              </div>
              <a
                href={`${API}/facture/download/${facture.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2 bg-[#003264] text-white rounded-lg hover:bg-[#002244] transition text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Télécharger
              </a>
            </div>
          ))}
        </div>
        
        {factures.length > 6 && (
          <p className="text-center text-sm text-gray-500 mt-4">
            Et {factures.length - 6} autre{factures.length - 6 > 1 ? "s" : ""} facture{factures.length - 6 > 1 ? "s" : ""}...
          </p>
        )}
      </div>
    </div>
  );
};

export const ParticuliersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();
  const [selectedMode, setSelectedMode] = useState(null); // null, 'ponctuel', 'abonnement'

  // Check if returning from login with pending reservation
  useEffect(() => {
    if (user) {
      const pendingReservation = localStorage.getItem('pendingReservation');
      if (pendingReservation) {
        const data = JSON.parse(pendingReservation);
        setSelectedMode(data.mode);
        toast.success("Compte créé ! Finalisez votre réservation.");
      }
    }
  }, [user]);

  // If mode is selected, show the appropriate flow
  if (selectedMode === 'ponctuel') {
    return (
      <div>
        <div className="pt-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <button 
              onClick={() => {
                setSelectedMode(null);
                localStorage.removeItem('pendingReservation');
              }}
              className="text-sm text-[#003264] hover:underline flex items-center gap-1"
            >
              ← Retour au choix
            </button>
          </div>
        </div>
        <EspaceParticulierFlow />
      </div>
    );
  }

  if (selectedMode === 'abonnement') {
    return (
      <div>
        <div className="pt-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <button 
              onClick={() => {
                setSelectedMode(null);
                localStorage.removeItem('pendingReservation');
              }}
              className="text-sm text-[#003264] hover:underline flex items-center gap-1"
            >
              ← Retour au choix
            </button>
          </div>
        </div>
        <AbonnementFlow />
      </div>
    );
  }

  // Default: show choice page (accessible to everyone)
  return (
    <div className="pt-16 min-h-screen bg-gray-50" data-testid="particuliers-page">
      <SEOHead 
        title="Ménage à Domicile Hérault — Particuliers"
        description="Service de ménage à domicile dans l'Hérault dès 18€/h grâce à l'avance immédiate du crédit d'impôt. Prestation ponctuelle ou abonnement régulier. Nettoyage de vitres, entretien maison. Réservez en ligne. Montpellier, Béziers, Sète et tout le 34."
        path="/particuliers"
        keywords="ménage à domicile Hérault, femme de ménage Montpellier, aide ménagère 34, nettoyage maison Hérault, entretien domicile Montpellier, abonnement ménage, nettoyage vitres particulier, crédit impôt ménage Hérault"
      />
      <div className="max-w-5xl mx-auto px-6 py-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          {user ? (
            <>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#003264] mb-4">
                Bonjour {user.prenom} !
              </h1>
              <p className="text-lg text-gray-600">
                Comment souhaitez-vous réserver votre prestation ?
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#003264] mb-4">
                Ménage à domicile dans l'Hérault
              </h1>
              <p className="text-lg text-gray-600">
                Réservez votre prestation de nettoyage en quelques clics — ponctuelle ou en abonnement
              </p>
            </>
          )}
        </div>

        {/* Avance immédiate banner */}
        <div className="max-w-4xl mx-auto mb-8" data-testid="particuliers-credit-banner">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-5 text-white flex items-center gap-5 flex-wrap md:flex-nowrap">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <BadgePercent className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg">Avance immédiate du crédit d'impôt</p>
              <p className="text-emerald-100 text-sm">Vous ne payez que 50% du montant. L'État verse directement le reste — aucune avance de trésorerie !</p>
            </div>
            <div className="bg-white/20 rounded-xl px-5 py-3 text-center flex-shrink-0">
              <p className="text-xs text-emerald-100">Ménage dès</p>
              <p className="text-2xl font-black">18€/h</p>
              <p className="text-xs text-emerald-100 line-through">au lieu de 36€/h</p>
            </div>
          </div>
        </div>

        {/* Choice Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          
          {/* Prestation ponctuelle */}
          <button
            onClick={() => setSelectedMode('ponctuel')}
            className="bg-white rounded-2xl border-2 border-gray-200 p-8 text-left transition-all hover:border-[#003264] hover:shadow-xl group"
            data-testid="choice-ponctuel"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#003264]/10 flex items-center justify-center mb-6 group-hover:bg-[#003264] transition-colors">
              <CalendarCheck className="w-8 h-8 text-[#003264] group-hover:text-white transition-colors" />
            </div>
            
            <h2 className="text-xl font-bold text-[#003264] mb-2">
              Prestation ponctuelle
            </h2>
            <p className="text-gray-600 mb-6">
              Réservez une intervention unique à la date de votre choix
            </p>
            
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Choisissez votre date et heure</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Idéal pour un besoin occasionnel</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-600">
                <BadgePercent className="w-4 h-4" />
                <span>Avance immédiate du crédit d'impôt -50%</span>
              </div>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Banknote className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-bold text-emerald-700 uppercase">Avec avance immédiate</p>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-gray-400 line-through">36€/h</span>
                <span className="text-2xl font-black text-emerald-600">18€<span className="text-sm font-normal">/heure</span></span>
              </div>
              <p className="text-xs text-gray-500 mt-1">ou <span className="line-through">3€/m²</span> → <strong className="text-emerald-600">1,50€/m²</strong></p>
            </div>
            
            <div className="flex items-center justify-between text-[#003264] font-semibold">
              <span>Réserver maintenant</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Abonnement */}
          <button
            onClick={() => setSelectedMode('abonnement')}
            className="bg-white rounded-2xl border-2 border-gray-200 p-8 text-left transition-all hover:border-[#003264] hover:shadow-xl group relative overflow-hidden"
            data-testid="choice-abonnement"
          >
            {/* Badge recommandé */}
            <div className="absolute top-4 right-4">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                RECOMMANDÉ
              </span>
            </div>
            
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors">
              <Repeat className="w-8 h-8 text-emerald-600 group-hover:text-white transition-colors" />
            </div>
            
            <h2 className="text-xl font-bold text-[#003264] mb-2">
              Abonnement régulier
            </h2>
            <p className="text-gray-600 mb-6">
              Planifiez vos interventions récurrentes et économisez encore plus
            </p>
            
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Interventions automatiques</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Tarifs dégressifs jusqu'à <strong className="text-emerald-600">-17%</strong></span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-600">
                <BadgePercent className="w-4 h-4" />
                <span>Avance immédiate du crédit d'impôt -50%</span>
              </div>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs font-bold text-emerald-700 uppercase">Avec avance immédiate</p>
                </div>
                <span className="px-2 py-0.5 bg-amber-400 text-amber-900 text-[10px] font-bold rounded-full uppercase">Le + choisi</span>
              </div>
              <p className="text-[10px] text-emerald-600 font-medium mb-1">Abonnement hebdomadaire</p>
              <div className="flex items-baseline gap-2">
                <span className="text-gray-400 line-through">32€/h</span>
                <span className="text-2xl font-black text-emerald-600">16€<span className="text-sm font-normal">/heure</span></span>
              </div>
              <p className="text-xs text-emerald-600 mt-1">-11% abonnement + crédit d'impôt combinés</p>
            </div>
            
            <div className="flex items-center justify-between text-[#003264] font-semibold">
              <span>Réserver maintenant</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>

        {/* Parrainage reminder - only for logged users */}
        {user && (
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 flex items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Gift className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Parrainez vos proches</h3>
                <p className="text-gray-600 text-sm">
                  Partagez votre code <span className="font-mono font-bold text-[#003264]">{user.code_parrainage}</span> et gagnez <span className="font-bold text-amber-600">15€</span> pour chaque filleul !
                </p>
              </div>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(user.code_parrainage);
                  toast.success("Code copié !");
                }}
                className="px-4 py-2 bg-white border border-amber-300 rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors"
              >
                Copier
              </button>
            </div>
          </div>
        )}

        {/* Mes factures - only for logged users */}
        {user && <MesFacturesSimple token={getToken()} />}

        {/* Info credit impot */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-5 py-3 rounded-full">
            <BadgePercent className="w-5 h-5 text-emerald-600" />
            <p className="text-sm text-emerald-700 font-medium">
              <strong>Avance immédiate</strong> du crédit d'impôt : vous ne payez que 50%, sans aucune démarche
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
